import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { PlusIcon, PencilSimpleIcon, TrashIcon, PercentIcon, TagIcon, XIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { inr, EMPTY_ITEM, CATEGORY_TONE, remainingPegs } from "./posHelpers";

export function MenuManager({ activeId, menu, canManage, onChanged }) {
  const [newItem, setNewItem] = useState(EMPTY_ITEM);
  const [newOpen, setNewOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkGstOpen, setBulkGstOpen] = useState(false);
  const [bulkGstRate, setBulkGstRate] = useState(12);
  const [categories, setCategories] = useState([]);
  const [catManagerOpen, setCatManagerOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");

  // Recipe editor state
  const [stockItems, setStockItems] = useState([]);
  const [recipeRows, setRecipeRows] = useState([]); // [{stock_item_id, qty_per_unit}]

  // Menu table search / category filter (must be declared BEFORE filteredForTable is computed)
  const [menuSearch, setMenuSearch] = useState("");
  const [menuCatFilter, setMenuCatFilter] = useState("all");

  useEffect(() => {
    if (!activeId) return;
    api.get("/pos/stock-items", { params: { property_id: activeId } })
      .then((r) => setStockItems(r.data))
      .catch(() => {});
    api.get("/pos/categories", { params: { property_id: activeId } })
      .then((r) => setCategories(r.data || []))
      .catch(() => setCategories([]));
  }, [activeId, newOpen, catManagerOpen]);

  const filteredForTable = (menu || []).filter((m) => {
    if (menuCatFilter !== "all" && m.category !== menuCatFilter) return false;
    const q = menuSearch.trim().toLowerCase();
    if (!q) return true;
    return (m.name || "").toLowerCase().includes(q) || (m.sku || "").toLowerCase().includes(q) || (m.category || "").toLowerCase().includes(q);
  });

  const addCategory = async () => {
    const name = newCatName.trim();
    if (!name) return;
    try {
      await api.post("/pos/categories", { property_id: activeId, name });
      const r = await api.get("/pos/categories", { params: { property_id: activeId } });
      setCategories(r.data || []);
      setNewCatName("");
      toast.success(`Category "${name}" added`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeCategory = async (cat) => {
    if (cat.builtin) return toast.info("Built-in category — add your own to override");
    if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    try {
      await api.delete(`/pos/categories/${cat.id}`);
      const r = await api.get("/pos/categories", { params: { property_id: activeId } });
      setCategories(r.data || []);
      toast.success("Category removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot delete"); }
  };

  useEffect(() => {
    if (editing?.id) {
      api.get(`/pos/menu/${editing.id}/recipe`).then((r) => setRecipeRows(r.data.recipe || [])).catch(() => setRecipeRows([]));
    } else {
      setRecipeRows([]);
    }
  }, [editing?.id]);

  const addRecipeRow = () => setRecipeRows([...recipeRows, { stock_item_id: "", qty_per_unit: 0 }]);
  const patchRecipeRow = (idx, patch) => setRecipeRows(recipeRows.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const removeRecipeRow = (idx) => setRecipeRows(recipeRows.filter((_, i) => i !== idx));
  const saveRecipe = async (menuId) => {
    if (!menuId) return;
    const clean = recipeRows.filter((r) => r.stock_item_id && Number(r.qty_per_unit) > 0).map((r) => ({ stock_item_id: r.stock_item_id, qty_per_unit: Number(r.qty_per_unit) }));
    await api.put(`/pos/menu/${menuId}/recipe`, { ingredients: clean });
  };

  const toggleSelected = (id) => setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const applyBulkGst = async () => {
    try {
      const r = await api.post("/pos/menu/bulk-gst", { ids: selectedIds, gst_rate: Number(bulkGstRate) || 0 });
      toast.success(`GST updated on ${r.data.updated} items`);
      setBulkGstOpen(false); setSelectedIds([]);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const [bulkCatOpen, setBulkCatOpen] = useState(false);
  const [bulkCatName, setBulkCatName] = useState("");
  const [bulkCatScope, setBulkCatScope] = useState("selected"); // "selected" | "visible"
  const applyBulkCategory = async () => {
    if (!bulkCatName) return toast.error("Pick a category");
    const ids = bulkCatScope === "visible" ? filteredForTable.map((m) => m.id) : selectedIds;
    if (!ids.length) return toast.error("Nothing to reassign");
    try {
      await api.post("/pos/menu/bulk-category", { ids, category: bulkCatName });
      toast.success(`Category set for ${ids.length} item(s)`);
      setBulkCatOpen(false); setSelectedIds([]);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const closeDialog = (v) => {
    setNewOpen(v);
    if (!v) { setEditing(null); setNewItem(EMPTY_ITEM); }
  };

  const saveItem = async () => {
    try {
      const payload = {
        ...newItem,
        price: Number(newItem.price) || 0,
        gst_rate: Number(newItem.gst_rate) || 0,
        stock: Number(newItem.stock) || 0,
        min_stock: Number(newItem.min_stock) || 5,
        unit_type: newItem.unit_type || "unit",
        bottle_size_ml: newItem.unit_type === "peg" ? Number(newItem.bottle_size_ml) || 750 : null,
        peg_size_ml: newItem.unit_type === "peg" ? Number(newItem.peg_size_ml) || 30 : null,
      };
      let savedId = editing?.id;
      if (editing) {
        await api.patch(`/pos/menu/${editing.id}`, payload);
        toast.success("Item updated");
      } else {
        const r = await api.post("/pos/menu", { property_id: activeId, ...payload });
        savedId = r.data.id;
        toast.success("Item added to menu");
      }
      // Persist recipe alongside menu-item save
      if (savedId) {
        try { await saveRecipe(savedId); } catch (e) { toast.error("Recipe save failed"); }
      }
      closeDialog(false);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteItem = async (mi) => {
    if (!window.confirm(`Remove '${mi.name}' from menu?`)) return;
    await api.delete(`/pos/menu/${mi.id}`);
    toast.success("Removed");
    onChanged?.();
  };

  return (
    <>
      <Card className="p-4 mb-4 flex justify-between items-center">
        <div>
          <p className="label-overline">Menu items</p>
          <p className="text-sm text-muted-foreground">Add dishes, set per-item GST rate, and enable stock tracking.</p>
        </div>
        {canManage && (
          <div className="flex gap-2 items-center">
            <Dialog open={catManagerOpen} onOpenChange={setCatManagerOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="pos-manage-categories-btn"><TagIcon size={12} className="mr-1" />Categories</Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto max-w-md">
                <DialogHeader><DialogTitle>Menu categories</DialogTitle></DialogHeader>
                <p className="text-xs text-muted-foreground mb-3">Group menu items by cuisine, section or outlet. Built-in defaults appear until you add your own.</p>
                <div className="flex gap-2 mb-4">
                  <Input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder="e.g. Chinese, Thali, Wines" data-testid="pos-cat-input" onKeyDown={(e) => e.key === "Enter" && addCategory()} />
                  <Button onClick={addCategory} data-testid="pos-cat-add-btn"><PlusIcon size={12} className="mr-1" />Add</Button>
                </div>
                <div className="space-y-1 max-h-60 overflow-y-auto">
                  {categories.map((c) => (
                    <div key={c.id} className="flex items-center justify-between rounded-md border border-border px-2 py-1.5 text-sm" data-testid={`pos-cat-row-${c.name}`}>
                      <span>{c.name}{c.builtin && <span className="ml-2 text-[10px] uppercase tracking-widest text-muted-foreground">Built-in</span>}</span>
                      {!c.builtin && (
                        <Button size="icon" variant="ghost" onClick={() => removeCategory(c)} data-testid={`pos-cat-delete-${c.name}`}><XIcon size={12} /></Button>
                      )}
                    </div>
                  ))}
                  {categories.length === 0 && <p className="text-xs text-muted-foreground italic">No categories yet — add one above.</p>}
                </div>
              </DialogContent>
            </Dialog>
            {(selectedIds.length > 0 || (menuSearch.trim() || menuCatFilter !== "all")) && (
              <Dialog open={bulkCatOpen} onOpenChange={(v) => { setBulkCatOpen(v); if (v) setBulkCatScope(selectedIds.length > 0 ? "selected" : "visible"); }}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="pos-menu-bulk-cat-btn">
                    <TagIcon size={12} className="mr-1" />
                    {selectedIds.length > 0 ? `Set category for ${selectedIds.length}` : `Set category for ${filteredForTable.length} visible`}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Reassign items to a category</DialogTitle></DialogHeader>
                  {selectedIds.length > 0 && (menuSearch.trim() || menuCatFilter !== "all") && (
                    <div className="rounded-md border border-border p-3 text-xs space-y-2" data-testid="pos-menu-bulk-cat-scope">
                      <p className="label-overline">Reassign scope</p>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" checked={bulkCatScope === "selected"} onChange={() => setBulkCatScope("selected")} className="accent-primary" data-testid="pos-menu-bulk-cat-scope-selected" />
                        <span>Only the <b>{selectedIds.length}</b> tick-selected item(s)</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" checked={bulkCatScope === "visible"} onChange={() => setBulkCatScope("visible")} className="accent-primary" data-testid="pos-menu-bulk-cat-scope-visible" />
                        <span>All <b>{filteredForTable.length}</b> item(s) matching the current search / category filter</span>
                      </label>
                    </div>
                  )}
                  <div>
                    <Label>Move to category</Label>
                    <Select value={bulkCatName} onValueChange={setBulkCatName}>
                      <SelectTrigger data-testid="pos-menu-bulk-cat-select"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                      <SelectContent>
                        {(categories.length ? categories : [{ id: "food", name: "Food" }, { id: "bar", name: "Bar" }]).map((c) => (
                          <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground mt-2">
                      About to move <b>{bulkCatScope === "visible" ? filteredForTable.length : selectedIds.length}</b> item(s) to <b>{bulkCatName || "—"}</b>.
                    </p>
                  </div>
                  <DialogFooter><Button onClick={applyBulkCategory} disabled={!bulkCatName} data-testid="pos-menu-bulk-cat-apply">Apply</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            {selectedIds.length > 0 && (
              <Dialog open={bulkGstOpen} onOpenChange={setBulkGstOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="pos-menu-bulk-gst-btn">
                    <PercentIcon size={12} className="mr-1" /> Set GST for {selectedIds.length}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Apply GST rate to {selectedIds.length} items</DialogTitle></DialogHeader>
                  <div>
                    <Label>GST rate (%)</Label>
                    <Select value={String(bulkGstRate)} onValueChange={(v) => setBulkGstRate(Number(v))}>
                      <SelectTrigger data-testid="pos-menu-bulk-gst-rate"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0% (Exempt)</SelectItem>
                        <SelectItem value="5">5% (Non-AC F&B)</SelectItem>
                        <SelectItem value="12">12%</SelectItem>
                        <SelectItem value="18">18% (Bar / AC F&B)</SelectItem>
                        <SelectItem value="28">28% (Luxury)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter><Button onClick={applyBulkGst} data-testid="pos-menu-bulk-gst-apply">Apply</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            <Dialog open={newOpen} onOpenChange={closeDialog}>
            <DialogTrigger asChild><Button data-testid="pos-menu-new-btn"><PlusIcon size={14} className="mr-1" /> Add item</Button></DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? "Edit item" : "New menu item"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name *</Label><Input value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} data-testid="pos-item-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category</Label>
                    <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                      <SelectTrigger data-testid="pos-item-category"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                      <SelectContent>
                        {(categories.length ? categories : [{ id: "food", name: "Food" }, { id: "bar", name: "Bar" }]).map((c) => (
                          <SelectItem key={c.id} value={c.name} data-testid={`pos-item-category-opt-${c.name}`}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>SKU (optional)</Label><Input value={newItem.sku || ""} onChange={(e) => setNewItem({ ...newItem, sku: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Price (₹)</Label><Input type="number" step="0.01" value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: e.target.value })} data-testid="pos-item-price" /></div>
                  <div><Label>GST rate (%)</Label><Input type="number" step="0.5" value={newItem.gst_rate} onChange={(e) => setNewItem({ ...newItem, gst_rate: e.target.value })} data-testid="pos-item-gst" /></div>
                </div>

                <div className="rounded-md border border-border p-3 space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={newItem.unit_type === "peg"}
                      onCheckedChange={(v) => setNewItem({
                        ...newItem,
                        unit_type: v ? "peg" : "unit",
                        category: v ? "Bar" : newItem.category,
                        track_stock: v ? true : newItem.track_stock,
                        bottle_size_ml: newItem.bottle_size_ml || 750,
                        peg_size_ml: newItem.peg_size_ml || 30,
                      })}
                      data-testid="pos-item-bar-toggle"
                    />
                    <span>Bar item — sold by the peg (deducts ml from bottle stock)</span>
                  </label>
                  {newItem.unit_type === "peg" && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Bottle size (ml)</Label>
                        <Select value={String(newItem.bottle_size_ml || 750)} onValueChange={(v) => setNewItem({ ...newItem, bottle_size_ml: Number(v) })}>
                          <SelectTrigger data-testid="pos-item-bottle-size"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="180">180 ml (Quart)</SelectItem>
                            <SelectItem value="375">375 ml (Half)</SelectItem>
                            <SelectItem value="500">500 ml</SelectItem>
                            <SelectItem value="750">750 ml (Std)</SelectItem>
                            <SelectItem value="1000">1000 ml (Litre)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Peg pour (ml)</Label>
                        <Select value={String(newItem.peg_size_ml || 30)} onValueChange={(v) => setNewItem({ ...newItem, peg_size_ml: Number(v) })}>
                          <SelectTrigger data-testid="pos-item-peg-size"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="30">30 ml (Small)</SelectItem>
                            <SelectItem value="60">60 ml (Large)</SelectItem>
                            <SelectItem value="90">90 ml (Patiala)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="col-span-2 text-[10px] text-muted-foreground">Enter starting stock in <strong>ml</strong> below (e.g. 5 bottles × 750ml = 3750 ml). Stock adjustments accept ml too.</p>
                    </div>
                  )}
                </div>

                <div className="rounded-md border border-border p-3 space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={newItem.track_stock} onCheckedChange={(v) => setNewItem({ ...newItem, track_stock: !!v })} data-testid="pos-item-stock-toggle" />
                    <span>Track inventory</span>
                  </label>
                  {newItem.track_stock && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">{newItem.unit_type === "peg" ? "Current stock (ml)" : "Current stock"}</Label>
                        <Input type="number" value={newItem.stock} onChange={(e) => setNewItem({ ...newItem, stock: e.target.value })} data-testid="pos-item-stock" />
                      </div>
                      <div>
                        <Label className="text-xs">{newItem.unit_type === "peg" ? "Min stock (ml, alert ≤)" : "Min stock (alert ≤)"}</Label>
                        <Input type="number" value={newItem.min_stock} onChange={(e) => setNewItem({ ...newItem, min_stock: e.target.value })} data-testid="pos-item-min-stock" />
                      </div>
                    </div>
                  )}
                </div>

                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={newItem.is_active} onCheckedChange={(v) => setNewItem({ ...newItem, is_active: !!v })} />
                  <span>Active on menu</span>
                </label>

                <div className="rounded-md border border-border p-3 space-y-2" data-testid="pos-item-recipe-panel">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Ingredients / recipe</p>
                      <p className="text-[10px] text-muted-foreground">Each time this item is sold, listed ingredients are auto-deducted from raw stock.</p>
                    </div>
                    <Button size="sm" variant="outline" type="button" onClick={addRecipeRow} data-testid="pos-recipe-add-row">
                      <PlusIcon size={10} className="mr-1" /> Add
                    </Button>
                  </div>
                  {recipeRows.length === 0 && <p className="text-[11px] italic text-muted-foreground">No ingredients set — this item won't touch raw stock.</p>}
                  {recipeRows.map((r, idx) => {
                    const si = stockItems.find((s) => s.id === r.stock_item_id);
                    return (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`pos-recipe-row-${idx}`}>
                        <div className="col-span-7">
                          <Select value={r.stock_item_id} onValueChange={(v) => patchRecipeRow(idx, { stock_item_id: v })}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`pos-recipe-item-${idx}`}><SelectValue placeholder="Pick raw item…" /></SelectTrigger>
                            <SelectContent>
                              {stockItems.length === 0 && <SelectItem value="__none" disabled>No raw items yet — add them in Store & stock</SelectItem>}
                              {stockItems.map((s) => (
                                <SelectItem key={s.id} value={s.id}>{s.name} ({s.unit}) · stock {s.stock}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-3">
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="qty"
                            value={r.qty_per_unit}
                            onChange={(e) => patchRecipeRow(idx, { qty_per_unit: e.target.value })}
                            className="h-8 text-xs"
                            data-testid={`pos-recipe-qty-${idx}`}
                          />
                        </div>
                        <div className="col-span-1 text-[10px] text-muted-foreground">{si?.unit || ""}</div>
                        <div className="col-span-1">
                          <Button size="icon" variant="ghost" type="button" onClick={() => removeRecipeRow(idx)} className="h-6 w-6 text-destructive"><TrashIcon size={10} /></Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <DialogFooter><Button onClick={saveItem} data-testid="pos-item-save-btn">{editing ? "Save changes" : "Add to menu"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </Card>

      <Card>
        <div className="p-3 flex flex-wrap gap-2 items-center border-b border-border" data-testid="pos-menu-search-bar">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={menuSearch}
              onChange={(e) => setMenuSearch(e.target.value)}
              placeholder="Search items — name, SKU or category…"
              className="w-full pl-9 pr-3 h-9 rounded-md border border-input bg-transparent text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              data-testid="pos-menu-search"
            />
          </div>
          <Select value={menuCatFilter} onValueChange={setMenuCatFilter}>
            <SelectTrigger className="h-9 w-44 text-xs" data-testid="pos-menu-cat-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {(categories.length ? categories : [{ id: "food", name: "Food" }, { id: "bar", name: "Bar" }]).map((c) => (
                <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <table className="w-full text-sm">
          <thead className="border-b border-border">
            <tr className="text-left">
              <th className="p-3 font-medium w-8"><input type="checkbox" checked={selectedIds.length === filteredForTable.length && filteredForTable.length > 0} onChange={(e) => setSelectedIds(e.target.checked ? filteredForTable.map((m) => m.id) : [])} data-testid="pos-menu-select-all" /></th>
              <th className="p-3 font-medium">Item</th>
              <th className="p-3 font-medium">Category</th>
              <th className="p-3 font-medium text-right">Price</th>
              <th className="p-3 font-medium text-right">GST</th>
              <th className="p-3 font-medium text-right">Stock</th>
              <th className="p-3 font-medium">Status</th>
              <th className="p-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredForTable.map((mi) => (
              <tr key={mi.id} className="border-b border-border/50 last:border-b-0">
                <td className="p-3"><input type="checkbox" checked={selectedIds.includes(mi.id)} onChange={() => toggleSelected(mi.id)} data-testid={`pos-menu-select-${mi.id}`} /></td>
                <td className="p-3">{mi.name} {mi.sku && <span className="text-[10px] text-muted-foreground ml-2 font-mono-plex">{mi.sku}</span>}</td>
                <td className="p-3"><Badge variant="outline" className={CATEGORY_TONE[mi.category] || ""}>{mi.category}</Badge></td>
                <td className="p-3 text-right font-mono-plex">{inr(mi.price)}</td>
                <td className="p-3 text-right font-mono-plex text-xs">{mi.gst_rate}%</td>
                <td className="p-3 text-right font-mono-plex text-xs">
                  {mi.track_stock ? (
                    mi.unit_type === "peg" ? (
                      <span className={remainingPegs(mi) < 3 ? "text-[hsl(var(--warning))]" : ""}>{remainingPegs(mi)} pegs<span className="text-muted-foreground"> · {mi.stock}ml</span></span>
                    ) : (
                      <span className={mi.stock < 5 ? "text-[hsl(var(--warning))]" : ""}>{mi.stock}</span>
                    )
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="p-3"><Badge variant="outline" className={mi.is_active ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" : "bg-muted text-muted-foreground"}>{mi.is_active ? "Active" : "Hidden"}</Badge></td>
                <td className="p-3 text-right">
                  {canManage && (
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => { setEditing(mi); setNewItem(mi); setNewOpen(true); }}><PencilSimpleIcon size={12} /></Button>
                      <Button size="icon" variant="ghost" onClick={() => deleteItem(mi)} className="text-destructive"><TrashIcon size={12} /></Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {menu.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-muted-foreground text-sm">No menu items yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </>
  );
}
